import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import appCss from "../styles.css?url";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { DeferredUI } from "@/components/DeferredUI";
import { DeferredAnalytics } from "@/components/DeferredAnalytics";
import { AuthProvider } from "@/hooks/use-auth";
import { CookieConsent } from "@/components/CookieConsent";
import { MeiMasterPopup } from "@/components/MeiMasterPopup";
import { ArrowRight, Home, Wrench, BookOpen } from "lucide-react";

function NotFoundComponent() {
  const topTools = [
    { slug: "calculadora-das-mei", title: "Calculadora DAS-MEI 2026" },
    { slug: "limite-faturamento-mei", title: "Limite de Faturamento MEI" },
    { slug: "calculadora-precificacao", title: "Calculadora de Precificação" },
    { slug: "validador-cnpj", title: "Validador de CNPJ" },
  ];
  const topArticles = [
    { slug: "o-que-e-mei", title: "O que é MEI?" },
    { slug: "como-formalizar-mei", title: "Como formalizar seu MEI" },
    { slug: "das-boleto-mei", title: "DAS do MEI: como gerar e pagar" },
    { slug: "limite-faturamento-mei", title: "Limite de faturamento: o que fazer no teto" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Erro 404
            </div>
            <h1 className="mt-6 font-display text-6xl md:text-8xl font-extrabold leading-none bg-gradient-hero bg-clip-text text-transparent">
              Página não encontrada
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto">
              A página que você procura não existe, foi movida ou ainda está em desenvolvimento.
              Veja algumas opções abaixo para continuar.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-primary-foreground shadow-elevated hover:opacity-90 transition"
              >
                <Home className="h-4 w-4" /> Voltar para o início
              </Link>
              <Link
                to="/ferramentas"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:bg-muted transition"
              >
                <Wrench className="h-4 w-4" /> Ver todas as ferramentas
              </Link>
              <Link
                to="/artigos"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:bg-muted transition"
              >
                <BookOpen className="h-4 w-4" /> Ler artigos
              </Link>
            </div>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                <Wrench className="h-3.5 w-3.5" /> Ferramentas populares
              </div>
              <ul className="mt-4 space-y-2">
                {topTools.map((t) => (
                  <li key={t.slug}>
                    <Link
                      to="/ferramentas/$slug"
                      params={{ slug: t.slug }}
                      className="group flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium hover:bg-muted transition"
                    >
                      <span>{t.title}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-x-0.5 transition" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                <BookOpen className="h-3.5 w-3.5" /> Artigos em destaque
              </div>
              <ul className="mt-4 space-y-2">
                {topArticles.map((a) => (
                  <li key={a.slug}>
                    <Link
                      to="/artigos/$slug"
                      params={{ slug: a.slug }}
                      className="group flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium hover:bg-muted transition"
                    >
                      <span className="line-clamp-1">{a.title}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-x-0.5 transition flex-shrink-0 ml-2" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <p className="mt-10 text-center text-xs text-muted-foreground">
            Acha que isso é um erro?{" "}
            <Link to="/contato" className="underline hover:text-foreground">
              Fale conosco
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "MEI Toolbox — Ferramentas grátis para Microempreendedor" },
      {
        name: "description",
        content:
          "Calculadora DAS, limite de faturamento, precificação, validador de CNPJ e dezenas de ferramentas grátis para o microempreendedor individual. Sem login.",
      },
      { name: "author", content: "MEI Toolbox" },
      { name: "google-site-verification", content: "f3Y1nvf1jXm7uCGIZLjJP7TOmDX3egXb7iI06XRjcDc" },
      { name: "p:domain_verify", content: "87815d8329329a4f132c83674453a230" },
      { property: "og:title", content: "MEI Toolbox — Ferramentas grátis para Microempreendedor" },
      {
        property: "og:description",
        content:
          "Calculadora DAS, limite de faturamento, precificação, validador de CNPJ e dezenas de ferramentas grátis para o microempreendedor individual. Sem login.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "MEI Toolbox — Ferramentas grátis para Microempreendedor" },
      {
        name: "twitter:description",
        content:
          "Calculadora DAS, limite de faturamento, precificação, validador de CNPJ e dezenas de ferramentas grátis para o microempreendedor individual. Sem login.",
      },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/EYkSzrmn3VWrt4YmY6Fp8sfE3Sc2/social-images/social-1777912945852-meitoolbox_1200x630_crop.webp",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/EYkSzrmn3VWrt4YmY6Fp8sfE3Sc2/social-images/social-1777912945852-meitoolbox_1200x630_crop.webp",
      },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "preload", as: "style", href: appCss },
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://analytics.ahrefs.com" },
      // Preload apenas da fonte de display (Manrope) usada no hero LCP.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
        href: "https://fonts.gstatic.com/s/manrope/v20/xn7gYHE41ni1AdIRggexSvfedN4.woff2",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://meitoolbox.com.br/#organization",
              name: "MEI Toolbox",
              url: "https://meitoolbox.com.br",
              logo: "https://meitoolbox.com.br/favicon.ico",
              description:
                "Ferramentas e artigos gratuitos para o Microempreendedor Individual brasileiro.",
            },
            {
              "@type": "WebSite",
              "@id": "https://meitoolbox.com.br/#website",
              url: "https://meitoolbox.com.br",
              name: "MEI Toolbox",
              inLanguage: "pt-BR",
              publisher: { "@id": "https://meitoolbox.com.br/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap";

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />

        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              var consent = null;
              try {
                consent = JSON.parse(localStorage.getItem('mt:cookie-consent'));
              } catch(e) {}
              var analyticsGranted = consent ? (consent.analytics ? 'granted' : 'denied') : 'denied';
              var marketingGranted = consent ? (consent.marketing ? 'granted' : 'denied') : 'denied';
              gtag('consent', 'default', {
                'analytics_storage': analyticsGranted,
                'ad_storage': marketingGranted,
                'ad_user_data': marketingGranted,
                'ad_personalization': marketingGranted,
                'wait_for_update': 500
              });
              gtag('set', 'ads_data_redaction', analyticsGranted === 'denied');
            `,
          }}
        />
        {/* Google AdSense — carregamento imediato no head a pedido do operador */}
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3380075088299150"
          crossOrigin="anonymous"
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=localStorage.getItem('theme');var d=window.matchMedia('(prefers-color-scheme: dark)').matches;if(t==='dark'||(!t&&d)){document.documentElement.classList.add('dark')}else{document.documentElement.classList.remove('dark')}}catch(e){}})();`,
          }}
        />
        {/* Carrega Google Fonts sem bloquear renderização (media="print" + swap via JS) */}
        <link rel="stylesheet" href={FONTS_HREF} media="print" data-async-css="fonts" />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){var l=document.querySelector('link[data-async-css="fonts"]');if(l){l.addEventListener('load',function(){l.media='all'});if(l.sheet){l.media='all'}}})();`,
          }}
        />
        <noscript>
          <link rel="stylesheet" href={FONTS_HREF} />
        </noscript>
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <AuthProvider>
      <div className="min-h-screen flex flex-col">
        <SiteHeader />
        <main className="flex-1 pb-32 sm:pb-40">
          <Outlet />
        </main>
        <SiteFooter />
        <DeferredUI />
        <DeferredAnalytics />
        <CookieConsent />
        <MeiMasterPopup />
      </div>
    </AuthProvider>
  );
}
